"use client"

import { useState, useEffect, useRef, useCallback } from 'react';

export interface SnakePosition {
  x: number;
  y: number;
}

export interface SnakeGameState {
  snake: SnakePosition[];
  food: SnakePosition;
  direction: 'UP' | 'DOWN' | 'LEFT' | 'RIGHT';
  isGameOver: boolean;
  isVictory: boolean;
  score: number;
  isPlaying: boolean;
  visitedCells: Set<string>;
  // Dimensions capturées au début de la partie pour éviter la triche
  gameStartDimensions?: {
    cols: number;
    rows: number;
    totalPanels: number;
    gridSize: number;
    gridWidth: number;
    gridHeight: number;
  };
}

interface UseSnakeGameProps {
  gridWidth: number;
  gridHeight: number;
  gridSize: number;
  onGameStateChange?: (state: SnakeGameState) => void;
  gameSpeed?: number;
}

export function useSnakeGame({ 
  gridWidth, 
  gridHeight, 
  gridSize, 
  onGameStateChange,
  gameSpeed = 300 
}: UseSnakeGameProps) {
  
  // Calculer le nombre de cases de la grille
  const cols = Math.floor(gridWidth / gridSize);
  const rows = Math.floor(gridHeight / gridSize);
  
  // État initial du jeu
  const [gameState, setGameState] = useState<SnakeGameState>({
    snake: [{ x: Math.floor(cols / 2), y: Math.floor(rows / 2) }],
    food: { x: Math.floor(cols * 0.75), y: Math.floor(rows * 0.75) },
    direction: 'RIGHT',
    isGameOver: false,
    isVictory: false,
    score: 0,
    isPlaying: false,
    visitedCells: new Set([`${Math.floor(cols / 2)},${Math.floor(rows / 2)}`])
  });
  
  // Calculer le nombre total de cases pour la victoire (serpent remplit toute la grille)
  const totalCells = cols * rows;

  const gameLoopRef = useRef<NodeJS.Timeout | null>(null);
  const nextDirectionRef = useRef<'UP' | 'DOWN' | 'LEFT' | 'RIGHT'>('RIGHT');

  // Générer une nouvelle position pour la nourriture
  const generateFood = useCallback((snake: SnakePosition[]): SnakePosition => {
    let newFood: SnakePosition;
    do {
      newFood = {
        x: Math.floor(Math.random() * cols),
        y: Math.floor(Math.random() * rows)
      };
    } while (snake.some(segment => segment.x === newFood.x && segment.y === newFood.y));
    return newFood;
  }, [cols, rows]);

  // Vérifier les collisions
  const checkCollision = useCallback((head: SnakePosition, snake: SnakePosition[]): boolean => {
    // Collision avec les murs
    if (head.x < 0 || head.x >= cols || head.y < 0 || head.y >= rows) {
      return true;
    }
    
    // Collision avec le corps du serpent
    return snake.some(segment => segment.x === head.x && segment.y === head.y);
  }, [cols, rows]);

  // Déplacer le serpent
  const moveSnake = useCallback(() => {
    setGameState(prevState => {
      if (!prevState.isPlaying || prevState.isGameOver) {
        return prevState;
      }

      const direction = nextDirectionRef.current;
      const head = prevState.snake[0];
      let newHead: SnakePosition;

      // Calculer la nouvelle position de la tête
      switch (direction) {
        case 'UP':
          newHead = { x: head.x, y: head.y - 1 };
          break;
        case 'DOWN':
          newHead = { x: head.x, y: head.y + 1 };
          break;
        case 'LEFT':
          newHead = { x: head.x - 1, y: head.y };
          break;
        case 'RIGHT':
          newHead = { x: head.x + 1, y: head.y };
          break;
      }

      // Vérifier les collisions
      if (checkCollision(newHead, prevState.snake)) {
        return { ...prevState, isGameOver: true, isPlaying: false };
      }

      // Nouveau serpent avec la nouvelle tête
      const newSnake = [newHead, ...prevState.snake];
      let newFood = prevState.food;
      let newScore = prevState.score;
      
      // Ajouter la nouvelle position aux cases visitées
      const newVisitedCells = new Set(prevState.visitedCells);
      newVisitedCells.add(`${newHead.x},${newHead.y}`);

      // Vérifier si la nourriture a été mangée
      if (newHead.x === prevState.food.x && newHead.y === prevState.food.y) {
        newFood = generateFood(newSnake);
        newScore += 10;
        
        // Vérifier la victoire - serpent remplit toute la grille
        if (newSnake.length >= totalCells) {
          return { 
            ...prevState, 
            snake: newSnake, 
            food: newFood, 
            score: newScore, 
            isVictory: true, 
            isPlaying: false,
            visitedCells: newVisitedCells
          };
        }
      } else {
        // Enlever la queue si pas de nourriture mangée
        newSnake.pop();
      }

      const newState = {
        ...prevState,
        snake: newSnake,
        food: newFood,
        direction,
        score: newScore,
        visitedCells: newVisitedCells
      };

      return newState;
    });
  }, [checkCollision, generateFood]);

  // Changer la direction (avec validation pour éviter les demi-tours)
  const changeDirection = useCallback((newDirection: 'UP' | 'DOWN' | 'LEFT' | 'RIGHT') => {
    const currentDirection = nextDirectionRef.current;
    
    // Empêcher les demi-tours
    const opposites = {
      'UP': 'DOWN',
      'DOWN': 'UP',
      'LEFT': 'RIGHT',
      'RIGHT': 'LEFT'
    };

    if (opposites[currentDirection] !== newDirection) {
      nextDirectionRef.current = newDirection;
    }
  }, []);

  // Démarrer le jeu
  const startGame = useCallback(() => {
    const initialSnake = [{ x: Math.floor(cols / 2), y: Math.floor(rows / 2) }];
    const initialFood = generateFood(initialSnake);
    
    // Capturer les dimensions au début de la partie pour éviter la triche
    const gameStartDimensions = {
      cols,
      rows,
      totalPanels: cols * rows,
      gridSize,
      gridWidth,
      gridHeight
    };
    
    setGameState({
      snake: initialSnake,
      food: initialFood,
      direction: 'RIGHT',
      isGameOver: false,
      isVictory: false,
      score: 0,
      isPlaying: true,
      visitedCells: new Set([`${Math.floor(cols / 2)},${Math.floor(rows / 2)}`]),
      gameStartDimensions
    });
    
    nextDirectionRef.current = 'RIGHT';
  }, [cols, rows, generateFood, gridSize, gridWidth, gridHeight]);

  // Arrêter le jeu
  const stopGame = useCallback(() => {
    setGameState(prev => ({ ...prev, isPlaying: false }));
  }, []);

  // Réinitialiser le jeu
  const resetGame = useCallback(() => {
    const initialSnake = [{ x: Math.floor(cols / 2), y: Math.floor(rows / 2) }];
    const initialFood = generateFood(initialSnake);
    
    setGameState(prev => ({
      snake: initialSnake,
      food: initialFood,
      direction: 'RIGHT',
      isGameOver: false,
      isVictory: false,
      score: 0,
      isPlaying: false,
      visitedCells: new Set([`${Math.floor(cols / 2)},${Math.floor(rows / 2)}`]),
      // Conserver les dimensions de la partie précédente si elles existent
      gameStartDimensions: prev.gameStartDimensions
    }));
    
    nextDirectionRef.current = 'RIGHT';
  }, [cols, rows, generateFood]);

  // Gestion du clavier
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Ignorer les événements si l'utilisateur tape dans un champ de saisie
      const target = event.target as HTMLElement;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      
      // Espace pour démarrer ou redémarrer le jeu
      if (event.key === ' ' || event.code === 'Space') {
        event.preventDefault();
        if (gameState.isGameOver || gameState.isVictory) {
          resetGame();
          setTimeout(() => startGame(), 100);
        } else if (!gameState.isPlaying) {
          startGame();
        }
        return;
      }

      if (!gameState.isPlaying) return;

      // Touche S pour direction bas pendant le jeu
      if (event.key === 's' || event.key === 'S') {
        event.preventDefault();
        changeDirection('DOWN');
        return;
      }

      switch (event.key) {
        case 'ArrowUp':
        case 'w':
        case 'W':
          event.preventDefault();
          changeDirection('UP');
          break;
        case 'ArrowDown':
          event.preventDefault();
          changeDirection('DOWN');
          break;
        case 'ArrowLeft':
        case 'a':
        case 'A':
          event.preventDefault();
          changeDirection('LEFT');
          break;
        case 'ArrowRight':
        case 'd':
        case 'D':
          event.preventDefault();
          changeDirection('RIGHT');
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [gameState.isPlaying, gameState.isGameOver, gameState.isVictory, changeDirection, startGame, resetGame]);

  // Boucle de jeu simple et stable
  useEffect(() => {
    if (gameState.isPlaying && !gameState.isGameOver && !gameState.isVictory) {
      gameLoopRef.current = setInterval(moveSnake, gameSpeed);
    } else {
      if (gameLoopRef.current) {
        clearInterval(gameLoopRef.current);
        gameLoopRef.current = null;
      }
    }

    return () => {
      if (gameLoopRef.current) {
        clearInterval(gameLoopRef.current);
        gameLoopRef.current = null;
      }
    };
  }, [gameState.isPlaying, gameState.isGameOver, moveSnake, gameSpeed]);

  // Notifier les changements d'état du jeu
  useEffect(() => {
    if (onGameStateChange) {
      onGameStateChange(gameState);
    }
  }, [gameState, onGameStateChange]);

  return {
    gameState,
    startGame,
    stopGame,
    resetGame,
    changeDirection
  };
}
